import ButtonGridConfig from '@/components/form-wizard/field-configs/ButtonGridConfig.vue'
import ButtonListConfig from '@/components/form-wizard/field-configs/ButtonListConfig.vue'
import CameraConfig from '@/components/form-wizard/field-configs/CameraConfig.vue'
import CheckboxConfig from '@/components/form-wizard/field-configs/CheckboxConfig.vue'
import CustomConfig from '@/components/form-wizard/field-configs/CustomConfig.vue'
import EmailInputConfig from '@/components/form-wizard/field-configs/EmailInputConfig.vue'
import FlowDataAggregatorConfig from '@/components/form-wizard/field-configs/FlowDataAggregatorConfig.vue'
import FlowFetchConfig from '@/components/form-wizard/field-configs/FlowFetchConfig.vue'
import FlowStringSplitConfig from '@/components/form-wizard/field-configs/FlowStringSplitConfig.vue'
import SetLocalStorageConfig from '@/components/form-wizard/field-configs/SetLocalStorageConfig.vue'
import LicensePlateInputConfig from '@/components/form-wizard/field-configs/LicensePlateInputConfig.vue'
import ListBrowserConfig from '@/components/form-wizard/field-configs/ListBrowserConfig.vue'
import MultipleEntryConfig from '@/components/form-wizard/field-configs/MultipleEntryConfig.vue'
import NumberInputConfig from '@/components/form-wizard/field-configs/NumberInputConfig.vue'
import NumberUnitInputConfig from '@/components/form-wizard/field-configs/NumberUnitInputConfig.vue'
import PhoneInputConfig from '@/components/form-wizard/field-configs/PhoneInputConfig.vue'
import SelectConfig from '@/components/form-wizard/field-configs/SelectConfig.vue'
import SummaryConfig from '@/components/form-wizard/field-configs/SummaryConfig.vue'
import TextInputConfig from '@/components/form-wizard/field-configs/TextInputConfig.vue'
import {
  createCheckboxConfig,
  createFetchConfig,
  createListBrowserConfig,
  createNumberConfig,
  createNumberUnitConfig,
  createPhoneConfig,
  createDataAggregatorConfig,
  createTextConfig,
  createWSBDConfig,
  createSummaryConfig,
  createEmailConfig,
  createStringSplitConfig,
  createSetLocalStorageConfig,
  createLicensePlateConfig,
  createSelectConfig,
  createButtonListConfig,
  createButtonGridConfig,
  createMultipleEntryConfig,
  createCameraConfig,
} from '@/configs/flow-field-configs'
import type { FlowField } from '@logistiek-zonder-papier/lzp-library'
import type { Component } from 'vue'

export type WizardElementConfig = {
  type: string
  buttonConfig: {
    label: string
    description: string
  }
  createField: (dto?: string) => FlowField
  fieldConfig: Component
}

export const wizardConfig: WizardElementConfig[] = [
  {
    type: 'text',
    buttonConfig: { label: 'Text', description: 'This is the text description' },
    createField: createTextConfig,
    fieldConfig: TextInputConfig,
  },
  {
    type: 'email',
    buttonConfig: { label: 'Email', description: 'Email field with email validation' },
    createField: createEmailConfig,
    fieldConfig: EmailInputConfig,
  },
  {
    type: 'licensePlate',
    buttonConfig: {
      label: 'License Plate',
      description: 'Licenseplate with configurable formatting and validation',
    },
    createField: createLicensePlateConfig,
    fieldConfig: LicensePlateInputConfig,
  },
  {
    type: 'number',
    buttonConfig: { label: 'Number', description: 'This is the number description' },
    createField: createNumberConfig,
    fieldConfig: NumberInputConfig,
  },
  {
    type: 'numberUnit',
    buttonConfig: { label: 'Number with Unit', description: 'Large number input with a unit' },
    createField: createNumberUnitConfig,
    fieldConfig: NumberUnitInputConfig,
  },
  {
    type: 'fetch',
    buttonConfig: {
      label: 'Fetch',
      description: 'Fetches data from an api and stores it in the flow context',
    },
    createField: createFetchConfig,
    fieldConfig: FlowFetchConfig,
  },
  {
    type: 'stringSplit',
    buttonConfig: {
      label: 'String Split',
      description: 'Splits the given source based on a split character',
    },
    createField: createStringSplitConfig,
    fieldConfig: FlowStringSplitConfig,
  },
  {
    type: 'setLocalStorage',
    buttonConfig: {
      label: 'Set Local Storage',
      description: 'Writes a value to localStorage once the page is completed',
    },
    createField: createSetLocalStorageConfig,
    fieldConfig: SetLocalStorageConfig,
  },
  {
    type: 'listBrowser',
    buttonConfig: {
      label: 'List',
      description: 'Lets the user select from a pre-fetch list available from the form context',
    },
    createField: createListBrowserConfig,
    fieldConfig: ListBrowserConfig,
  },
  {
    type: 'select',
    buttonConfig: {
      label: 'Select',
      description: 'Dropdown with options from a list available in the form context',
    },
    createField: createSelectConfig,
    fieldConfig: SelectConfig,
  },
  {
    type: 'buttonList',
    buttonConfig: {
      label: 'Button list',
      description: 'Lets the user pick one of several buttons, each storing its own value',
    },
    createField: createButtonListConfig,
    fieldConfig: ButtonListConfig,
  },
  {
    type: 'buttonGrid',
    buttonConfig: {
      label: 'Button grid',
      description: 'Grid of icon buttons; pressing one stores its value and continues the flow',
    },
    createField: createButtonGridConfig,
    fieldConfig: ButtonGridConfig,
  },
  {
    type: 'multipleEntry',
    buttonConfig: {
      label: 'Multiple entry',
      description: 'Lets the user add and remove several text or number values',
    },
    createField: createMultipleEntryConfig,
    fieldConfig: MultipleEntryConfig,
  },
  {
    type: 'wsbd',
    buttonConfig: { label: 'WSBD', description: 'This is the custom description' },
    createField: createWSBDConfig,
    fieldConfig: CustomConfig,
  },
  {
    type: 'checkbox',
    buttonConfig: { label: 'Checkbox', description: 'Checkbox with a html support for its label' },
    createField: createCheckboxConfig,
    fieldConfig: CheckboxConfig,
  },
  {
    type: 'phone',
    buttonConfig: { label: 'Phone', description: 'Phone number field' },
    createField: createPhoneConfig,
    fieldConfig: PhoneInputConfig,
  },
  {
    type: 'summary',
    buttonConfig: { label: 'Summary', description: 'Shows a summary of the result of a flow' },
    createField: createSummaryConfig,
    fieldConfig: SummaryConfig,
  },
  {
    type: 'camera',
    buttonConfig: { label: 'Camera Component', description: 'add photos to the flow' },
    createField: createCameraConfig,
    fieldConfig: CameraConfig,
  },
  {
    type: 'aggregator',
    buttonConfig: { label: 'Data aggregator', description: 'Combines arrays in elements' },
    createField: createDataAggregatorConfig,
    fieldConfig: FlowDataAggregatorConfig,
  },
]
